import React, { useState } from 'react';
import ModalPortal from '../../../components/ModalPortal';

/**
 * CreateDeploymentModal component - Modal for creating new deployments
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {string} props.newDepName - Name for new deployment
 * @param {Function} props.setNewDepName - Setter for new deployment name
 * @param {string} props.newDepFamilyId - Selected family ID
 * @param {Function} props.setNewDepFamilyId - Setter for family ID
 * @param {Array} props.linkedFamilies - Available families to link
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {React.RefObject} props.overlayMouseDown - Ref for tracking overlay mouse down
 */
const CreateDeploymentModal = ({
    isOpen,
    onClose,
    onSubmit,
    newDepName,
    setNewDepName,
    newDepFamilyId,
    setNewDepFamilyId,
    newDepPassword,
    setNewDepPassword,
    linkedFamilies,
    isSubmitting,
    overlayMouseDown
}) => {
    if (!isOpen) return null;

    return (
        <ModalPortal>
        <div
            className="modal-overlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) overlayMouseDown.current = true;
            }}
            onMouseUp={(e) => {
                if (e.target === e.currentTarget && overlayMouseDown.current) {
                    onClose();
                }
                overlayMouseDown.current = false;
            }}
        >
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '420px', padding: '1.25rem' }}>
                <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Create Deployment</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
                    Deploy a family with its specific layout version tag.
                </p>

                <form onSubmit={onSubmit} className="importer-form" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <FormField
                        label="Deployment Name"
                        required
                        placeholder="e.g. Sedan Configurator Prod"
                        value={newDepName}
                        onChange={e => setNewDepName(e.target.value)}
                        autoFocus
                    />

                    <FormField
                        label="Family"
                        as="select"
                        required
                        value={newDepFamilyId}
                        onChange={e => setNewDepFamilyId(e.target.value)}
                        disabled={linkedFamilies.length === 0}
                    >
                        {linkedFamilies.length === 0 ? (
                            <option value="">No families found for this project. Link a family in Projects first.</option>
                        ) : (
                            linkedFamilies.map(fam => (
                                <option key={fam.id} value={fam.id.toString()}>{fam.name}</option>
                            ))
                        )}
                    </FormField>

                    <PasswordField
                        label="Demo password"
                        hint="Optional. Guests need this password to open the hosted demo and any iframe embed."
                        value={newDepPassword}
                        onChange={e => setNewDepPassword(e.target.value)}
                    />

                    <div className="modal-actions" style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            type="submit"
                            className="btn-primary"
                            style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                            disabled={isSubmitting || linkedFamilies.length === 0}
                        >
                            {isSubmitting ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
};

/**
 * Generic form field component
 */
const FormField = ({ label, as = 'input', required, placeholder, value, onChange, disabled, children, autoFocus }) => {
    const InputElement = as;
    
    return (
        <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                {label}
                {required && <span style={{ color: 'var(--error)', marginLeft: '0.25rem' }}>*</span>}
            </label>
            {as === 'select' ? (
                <select
                    className="input-field"
                    value={value}
                    onChange={onChange}
                    required={required}
                    style={{ margin: 0, width: '100%', cursor: 'pointer' }}
                    disabled={disabled}
                >
                    {children}
                </select>
            ) : (
                <InputElement
                    className="input-field"
                    value={value}
                    onChange={onChange}
                    required={required}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    style={{ margin: 0 }}
                    disabled={disabled}
                />
            )}
        </div>
    );
};

export default CreateDeploymentModal;
export { FormField, PasswordField };

const PasswordField = ({ label, hint, value, onChange }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!value) return;
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                {label}
            </label>
            {hint && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '-0.25rem 0 0.5rem 0' }}>{hint}</p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                    type="text"
                    className="input-field"
                    value={value}
                    onChange={onChange}
                    placeholder="Optional guest password"
                    style={{ margin: 0, flex: 1 }}
                    autoComplete="off"
                />
                <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCopy}
                    disabled={!value}
                    style={{
                        padding: '0.5rem 1rem',
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        background: copied ? 'var(--success)' : 'transparent',
                        color: copied ? 'white' : 'var(--text-secondary)'
                    }}
                >
                    {copied
                        ? <><i className="fa-solid fa-check"></i> Copied</>
                        : <><i className="fa-regular fa-copy"></i> Copy password</>}
                </button>
            </div>
        </div>
    );
};
